'use client';

import type { FC } from 'react';
import { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { usePermission } from '~/app/auth/hooks/usePermission';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import {
  normalizeAssetLinkCandidates,
  resolveKmsLinkerCandidates,
} from '~/app/services/kms/kms-document.utils';
import { useKmsAssetLinkEditor } from '../../hooks/useKmsAssetLinkEditor';
import { KmsAssetLinkCandidatesList } from '../KmsAssetLinkCandidatesList/KmsAssetLinkCandidatesList';

const { Text } = Typography;

export interface KmsAssetLinkCardProps {
  document: GetKmsDocumentStatusResponse;
  organizationId: string | null;
  isSuperAdmin: boolean;
}

export const KmsAssetLinkCard: FC<KmsAssetLinkCardProps> = ({
  document,
  organizationId,
  isSuperAdmin,
}) => {
  const { t } = useTranslation('kms');
  const { hasPermission } = usePermission();
  const canRunLinker = hasPermission(PermissionKey.DOCUMENTS_UPLOAD);
  const editor = useKmsAssetLinkEditor({
    document,
    organizationId,
    isSuperAdmin,
  });

  if (!organizationId) {
    return null;
  }

  const showRunLinker =
    editor.canRerunLink && !editor.linkSkipped && !editor.linkScope;

  const showAssetCandidates = useMemo(() => {
    if (editor.awaitingLink || editor.linkSkipped) {
      return false;
    }
    if (editor.linkScope) {
      return true;
    }
    return (
      normalizeAssetLinkCandidates(resolveKmsLinkerCandidates(document))
        .length > 0
    );
  }, [document, editor.awaitingLink, editor.linkSkipped, editor.linkScope]);

  return (
    <Card
      className="kms-section-card"
      title={t('detail.assetLink.title')}
      extra={
        editor.linkSkipped ? (
          <Tag>{t('detail.assetLink.skipped')}</Tag>
        ) : editor.awaitingLink ? (
          <Tag color="blue">{t('detail.assetLink.waiting')}</Tag>
        ) : editor.linkScope ? (
          <Tag color="green">{t('detail.assetLink.linkerDone')}</Tag>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">{t('detail.assetLink.subtitle')}</Text>

        {editor.linkSkipped ? (
          <Alert
            type="info"
            showIcon
            message={t('detail.assetLink.skippedTitle')}
            description={t('detail.assetLink.skippedDescription', {
              type: editor.confirmedType ?? '—',
            })}
          />
        ) : null}

        {showRunLinker ? (
          <Alert
            type="warning"
            showIcon
            message={t('detail.assetLink.runLinkerTitle')}
            description={t('detail.assetLink.runLinkerHelp')}
            action={
              <Button
                type="primary"
                icon={<LinkOutlined />}
                loading={editor.isRerunningLink}
                disabled={!canRunLinker}
                onClick={() => void editor.onRerunLink()}
              >
                {t('detail.assetLink.runLinker')}
              </Button>
            }
          />
        ) : null}

        {showAssetCandidates ? (
          <KmsAssetLinkCandidatesList
            document={document}
            organizationId={organizationId}
          />
        ) : null}

        {editor.canRerunLink && editor.linkScope ? (
          <Button
            icon={<LinkOutlined />}
            loading={editor.isRerunningLink}
            disabled={!canRunLinker}
            onClick={() => void editor.onRerunLink()}
          >
            {t('detail.assetLink.rerunLinker')}
          </Button>
        ) : null}

        {!canRunLinker && editor.canRerunLink ? (
          <Text type="secondary">
            {t('detail.assetLink.runLinkerReadOnly')}
          </Text>
        ) : null}

        {editor.manualAssetId &&
        editor.manualAssetId !== editor.linkedAssetId ? (
          <Alert
            type="info"
            showIcon
            message={t('detail.assetLink.uploadHintTitle')}
            description={t('detail.assetLink.uploadHintDescription', {
              assetId: editor.manualAssetId,
            })}
          />
        ) : null}

        {editor.canEdit ? (
          <>
            <Radio.Group
              value={editor.scopeKind}
              onChange={(event) => editor.setScopeKind(event.target.value)}
            >
              <Radio.Button value="global">
                {t('detail.assetLink.orgWide')}
              </Radio.Button>
              <Radio.Button value="asset">
                {t('detail.assetLink.specificAsset')}
              </Radio.Button>
            </Radio.Group>

            {editor.scopeKind === 'asset' ? (
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%', maxWidth: 420 }}
                placeholder={t('detail.assetLink.assetPlaceholder')}
                loading={editor.assetsLoading}
                options={editor.assetOptions}
                value={editor.assetId}
                onChange={(value) => editor.setAssetId(value)}
              />
            ) : null}

            <Button
              type="primary"
              loading={editor.isSaving}
              onClick={() => void editor.onSave()}
            >
              {t('detail.assetLink.save')}
            </Button>
          </>
        ) : editor.linkedAssetId ? (
          <Alert
            type="success"
            showIcon
            message={t('detail.assetLink.currentLink')}
            description={
              editor.linkedAssetName?.trim() ||
              t('detail.candidateAsset', { assetId: editor.linkedAssetId })
            }
          />
        ) : null}
      </Space>
    </Card>
  );
};
